import React, { useRef, useState, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import { FaTrash, FaVideo, FaPlay, FaPause, FaSave, FaSync, FaTimes, FaUpload } from 'react-icons/fa';
import { Button } from 'react-bootstrap';
import maverickPortalhttp from '../../utils/webService/webService';
import maverickPortalApiEndPoint from '../../config/apiEndPoint';
import { clearVideoUploadSession } from '../../utils/videoUploadSession';
import { formatRemainingUploadTime } from '../../utils/multipartVideoUpload';
 
/**
 * Prefer native camera on phones/tablets; desktop keeps in-page getUserMedia recorder.
 */
export function isMobileDevice() {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const mobileUa =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const coarsePointer =
    typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
  return mobileUa || coarsePointer;
}
 
export const VideoUploadContainer = styled.div`
  margin-top: 1rem;
  border-radius: 12px;
  padding: 12px;
  background: #fff;
`;
 
export const SuccessRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;
 
export const SuccessMessage = styled.span`
  color: #9ebe21;
  font-weight: 500;
  font-size: 13px;
  display: flex;
  align-items: center;
`;
 
export const DeleteButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #888;
  margin-left: 12px;
  display: flex;
  align-items: center;
  &:hover {
    color: #333;
  }
`;
 
export const UploadButton = styled.button`
  background: #fff;
  color: #9ebe21;
  border: 2px solid #9ebe21;
  font-weight: bold;
  border-radius: 6px;
  padding: 12px 16px;
  width: 30%;
  cursor: pointer;
  font-size: 14px;
  text-align: center;
  display: flex;
  justify-content: center;
  gap: 8px;
  &:hover {
    background: #f6fbe9;
  }
`;
const VideoWrapper = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: 12px;
`;
 
const VideoPreview = styled.video`
  width: 100%;
  height: 200px;
  border-radius: 12px;
  background: #000;
`;
 
const OverlayControls = styled.div`
  position: absolute;
  bottom: 32px;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: space-around; // Change to space-around for even spacing
  align-items: center;
  z-index: 10;
`;
 
const Timer = styled.div`
  color: #fff;
  font-size: 22px;
  font-weight: bold;
  margin-bottom: 8px;
  text-align: center;
`;
 
const RoundButton = styled.button`
  pointer-events: all;
  width: 46px;
  height: 46px;
  border-radius: 50%;
  border: none;
  background: #9ebe21;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  transition: background 0.2s ease;
`;
const GreenRoundButton = styled.button`
  background: #9ebe21;
  width: 70px;
  height: 40px;
  border-radius: 38%;
  border: none;
  font-size: 12px;
  color: #fff;
  &:hover {
    background: #7fa61a;
  }
`;

const TimerDisplay = styled.div`
  position: fixed;
  bottom: calc(64px + 16px + env(safe-area-inset-bottom, 0px));
  width: 100%;
  text-align: center;
  font-size: 22px;
  font-weight: bold;
  color: #fff;
  z-index: 20;
`;
const ButtonSubmit = styled(Button)`
  background: #9ebe21;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 12px 16px;
  cursor: pointer;
  font-size: 14px;
  text-align: center;
  display: flex;
  justify-content: center;
  gap: 8px;
  &:hover {
    background: #fff;
    color: #9ebe21;
    border: 2px solid #9ebe21;
  }
`;
 
const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: rgba(50, 50, 50, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10000;
  backdrop-filter: blur(4px);
 
  .cross {
    position: relative;
    width: 16px;
    height: 16px;
  }
 
  .cross::before,
  .cross::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 16px;
    height: 2.5px;
    background: #fff;
    border-radius: 2px;
    transform-origin: center;
  }
 
  .cross::before {
    transform: translate(-50%, -50%) rotate(45deg);
  }
 
  .cross::after {
    transform: translate(-50%, -50%) rotate(-45deg);
  }
 
  &:hover {
    background: rgba(0, 0, 0, 0.8);
    transform: scale(1.05);
  }
`;
 
const ButtonContainer = styled.div`
  position: fixed;
  bottom: calc(16px + env(safe-area-inset-bottom, 0px));
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  z-index: 1000;

  box-sizing: border-box;
  background: rgba(50, 50, 50, 0.6);
  backdrop-filter: blur(6px);
  @media (max-width: 480px) {
    flex-direction: row;
    gap: 12px;
    bottom: calc(env(safe-area-inset-bottom, 0px));
  }
`;
 
const CenterButton = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
`;
 
const FlipButton = styled(RoundButton)`
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(4px);
  color: #fff;
  transition: all 0.2s ease;
 
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.1);
  }
`;
 
const ProgressCircle = styled.div`
  width: 45px;
  height: 45px;
  border-radius: 50%;
  background: #f0f0f0;
  position: relative;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: #7c7c7c;
  font-weight: bold;
`;
 
const ProgressText = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;
 
const VideoUploadQuestion = ({
  questionId,
  mediaUrls,
  setMediaUrls,
  setVideoBlobs,
  videoBlobs,
  setRecordingStates,
  recordingStates,
  selectedFiles,
  setSelectedFiles,
  setVideoUploading,
  videoUploading,
  uploadVideo,
  stopVideoRecording,
  successMsg,
  setVideoUploadSuccess,
  setPreAssignedImageUrls,
  preAssignedImageUrl,
  setMediaUploading,
  onRequestStartRecording,
  disabled = false,
  savedUploadProgress = 0,
  uploadStage: uploadStageProp,
  remainingUploadSeconds: remainingUploadSecondsProp = null,
  uploadActionError = '',
  connectionMessageStage = null,
  canResumeVideoUpload = false,
  onResumeVideoUpload,
  onDeleteIncompleteVideoUpload,
  onUploadInterrupted,
  onClearUploadActionError,
}) => {
  const videoUrl = preAssignedImageUrl || mediaUrls[questionId];
  const [recording, setRecording] = useState(false);
  const [paused, setPaused] = useState(false);
  const [stream, setStream] = useState(null);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const chunksRef = useRef([]);
  const videoRef = useRef();
  const [facingMode, setFacingMode] = useState('user');
  const [deleteclicked, setDeleteClicked] = useState(false);
  const [showSave, setShowSave] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showRecorder, setShowRecorder] = useState(false);
  const [previewFromUpload, setPreviewFromUpload] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [remainingUploadSeconds, setRemainingUploadSeconds] = useState(null);
  const [uploadStage, setUploadStage] = useState('preparing');
  const [chance1, setChance1] = useState(false);
  const [chance2, setChance2] = useState(false);
  const [checkFileApiMessageStage, setCheckFileApiMessageStage] = useState(null);
  const [netDown, setNetDown] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const errorMessageTimeoutRef = useRef(null);

  const clearErrorMessage = useCallback(() => {
    if (errorMessageTimeoutRef.current) {
      clearTimeout(errorMessageTimeoutRef.current);
      errorMessageTimeoutRef.current = null;
    }
    setErrorMessage('');
  }, []);

  const showTempErrorMessage = useCallback(
    message => {
      if (errorMessageTimeoutRef.current) {
        clearTimeout(errorMessageTimeoutRef.current);
      }
      setErrorMessage(message);
      errorMessageTimeoutRef.current = setTimeout(() => {
        setErrorMessage('');
        errorMessageTimeoutRef.current = null;
      }, 5000);
    },
    []
  );
 
  const [recordedUrl, setRecordedUrl] = useState(() => {
    return mediaUrls[questionId] || null;
  });
  const [timer, setTimer] = useState(0);
  const timerRef = useRef();
  const [availableCameras, setAvailableCameras] = useState({ front: true, back: true });
  const [isUploading, setIsUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const wasDeletedRef = useRef(false);
  const [retryCount, setRetryCount] = useState(3);
  const connectionCheckInterval = useRef(null);
  const [isCheckingConnection, setIsCheckingConnection] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [connectionType, setConnectionType] = useState(navigator.connection?.type || 'unknown');
 
  useEffect(() => {
    if (recording && !paused) {
      timerRef.current = setInterval(() => setTimer(t => t + 1), 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [recording, paused]);

  useEffect(() => {
    return () => {
      if (errorMessageTimeoutRef.current) {
        clearTimeout(errorMessageTimeoutRef.current);
      }
    };
  }, []);
 
  const formatTime = sec => {
    const mins = Math.floor(sec / 60);
    const remainingSecs = sec % 60;
    return `${String(mins).padStart(2, '0')}:${String(remainingSecs).padStart(2, '0')}`;
  };
 
  const pauseRecording = () => {
    if (mediaRecorder?.state === 'recording') {
      mediaRecorder.pause();
      setPaused(true);
    }
  };
 
  const resumeRecording = () => {
    if (mediaRecorder?.state === 'paused') {
      mediaRecorder.resume();
      setPaused(false);
    }
  };
 
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    const handleConnectionChange = () => {
      if (navigator.connection) {
        setConnectionType(navigator.connection.type);
      }
    };
 
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    if (navigator.connection) {
      navigator.connection.addEventListener('change', handleConnectionChange);
    }
 
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (navigator.connection) {
        navigator.connection.removeEventListener('change', handleConnectionChange);
      }
    };
  }, []);
 
  const checkInternetConnection = async () => {
    setIsCheckingConnection(true);
    try {
      // Check if we're online
      if (!navigator.onLine) {
        return false;
      }
 
      // Check connection type if available
      if (navigator.connection) {
        const connection = navigator.connection;
        // Consider metered connections as potentially unreliable
        // if (connection.metered) {
        //   return false;
        // }
        // Consider slow connections as potentially unreliable
        // if (connection.downlink < 1) {
        //   return false;
        // }
      }
 
      return true;
    } catch (error) {
      console.error('Error checking connection:', error);
      return false;
    } finally {
      setIsCheckingConnection(false);
    }
  };
 
  const checkAvailableCameras = async () => {
    try {
      // Check front camera
      const frontStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user' }
      });
      frontStream.getTracks().forEach(track => track.stop());
      setAvailableCameras(prev => ({ ...prev, front: true }));
    } catch (err) {
      setAvailableCameras(prev => ({ ...prev, front: false }));
    }
 
    try {
      // Check back camera
      const backStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      backStream.getTracks().forEach(track => track.stop());
      setAvailableCameras(prev => ({ ...prev, back: true }));
    } catch (err) {
      setAvailableCameras(prev => ({ ...prev, back: false }));
    }
  };
 
  useEffect(() => {
    if (showRecorder && !stream && !isMobileDevice()) {
      handleStartRecorder();
    }
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [showRecorder]);

  useEffect(() => {
    if (!showRecorder) return undefined;

    const scrollY = window.scrollY || window.pageYOffset || 0;
    const originalBodyOverflow = document.body.style.overflow;
    const originalBodyPosition = document.body.style.position;
    const originalBodyTop = document.body.style.top;
    const originalBodyWidth = document.body.style.width;
    const originalBodyTouchAction = document.body.style.touchAction;
    const originalDocumentOverflow = document.documentElement.style.overflow;
    const originalOverscrollBehavior = document.documentElement.style.overscrollBehavior;

    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.body.style.touchAction = 'none';
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.overscrollBehavior = 'none';

    // Best-effort: block pinch/double-tap zoom on mobile browsers.
    // Note: some iOS Safari behaviors may still bypass CSS-only controls,
    // so we also intercept gesture/touch events.
    const preventZoomGesture = e => {
      try {
        e.preventDefault();
      } catch {
        /* ignore */
      }
      return false;
    };

    const preventMultiTouchMove = e => {
      // Only prevent multi-touch moves (pinch gestures).
      if (e.touches && e.touches.length > 1) {
        try {
          e.preventDefault();
        } catch {
          /* ignore */
        }
      }
    };

    const preventDoubleTapZoom = e => {
      try {
        e.preventDefault();
      } catch {
        /* ignore */
      }
      return false;
    };

    window.addEventListener('gesturestart', preventZoomGesture);
    window.addEventListener('gesturechange', preventZoomGesture);
    window.addEventListener('dblclick', preventDoubleTapZoom);
    window.addEventListener('touchmove', preventMultiTouchMove, { passive: false });

    return () => {
      document.body.style.overflow = originalBodyOverflow;
      document.body.style.position = originalBodyPosition;
      document.body.style.top = originalBodyTop;
      document.body.style.width = originalBodyWidth;
      document.body.style.touchAction = originalBodyTouchAction;
      document.documentElement.style.overflow = originalDocumentOverflow;
      document.documentElement.style.overscrollBehavior = originalOverscrollBehavior;
      window.scrollTo(0, scrollY);

      window.removeEventListener('gesturestart', preventZoomGesture);
      window.removeEventListener('gesturechange', preventZoomGesture);
      window.removeEventListener('dblclick', preventDoubleTapZoom);
      window.removeEventListener('touchmove', preventMultiTouchMove);
    };
  }, [showRecorder]);
 
  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);
 
  useEffect(() => {
    if (mediaUrls[questionId]) {
      setRecordedUrl(mediaUrls[questionId]);
    }
  }, [mediaUrls, questionId]);
 
  const startVideoRecording = async questionId => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
 
      setStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
 
      const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')
        ? 'video/webm;codecs=vp8,opus'
        : 'video/webm';
 
      const recorder = new MediaRecorder(stream, {
        mimeType,
        videoBitsPerSecond: 2500000,
        audioBitsPerSecond: 128000
      });
 
      setMediaRecorder(recorder);
      chunksRef.current = [];
 
      recorder.ondataavailable = e => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };
 
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        const url = URL.createObjectURL(blob);
 
        setMediaUrls(prev => ({ ...prev, [questionId]: url }));
        setVideoBlobs(prev => ({ ...prev, [questionId]: blob }));
        setRecordedUrl(url);
        setPreviewFromUpload(false);
        setShowSave(true);
      };
 
      recorder.start(1000);
      setRecording(true);
      setPaused(false);
    } catch (err) {
      console.error('Media error:', err);
      if (err.name === 'NotAllowedError') {
        alert('Please allow camera and microphone access.');
      } else {
        alert('Error accessing media devices.');
      }
    }
  };
 
  const handleDeleteuri = () => {
    // const connection = navigator.connection;
    // const connectionLooksDown =
    //   Boolean(connection) && (connection.metered || (typeof connection.downlink === 'number' && connection.downlink < 1));
    if (!navigator.onLine) {
      alert('Network is down. Please try connecting to another network');
      return;
    }
    wasDeletedRef.current = true; // sync update
 
    setTimeout(async () => {
      // Call the delete API
      const user_ID = localStorage.getItem('user_id');
      const payload = {
        user_ID: user_ID,
        question_ID: questionId
      };
 
      await maverickPortalhttp.makeRequest(
        maverickPortalApiEndPoint.deleteMedia.url,
        maverickPortalApiEndPoint.deleteMedia.methodType,
        payload
      );
      clearVideoUploadSession(questionId).catch(() => {});
 
      // Reset all states
      setRecordedUrl(null);
      setRecording(false);
      setPaused(false);
      setTimer(0);
      setShowDelete(false);
      setShowSave(false);
      setUploadSuccess(false);
      chunksRef.current = [];
      setDeleteClicked(true);
 
      if (mediaRecorder) {
        if (mediaRecorder.state !== 'inactive') {
          mediaRecorder.stop();
        }
        setMediaRecorder(null);
      }
    }, 100);
  };
 
  const handleDelete = async () => {
    try {
      // const connection = navigator.connection;
      // const connectionLooksDown =
      //   Boolean(connection) && (connection.metered || (typeof connection.downlink === 'number' && connection.downlink < 1));
      if (!navigator.onLine) {
        alert('Network is down. Please try connecting to another network');
        return;
      }
      // Call the delete API
      const user_ID = localStorage.getItem('user_id');
      const payload = {
        user_ID: user_ID,
        question_ID: questionId
      };
 
      await maverickPortalhttp.makeRequest(
        maverickPortalApiEndPoint.deleteMedia.url,
        maverickPortalApiEndPoint.deleteMedia.methodType,
        payload
      );
      clearVideoUploadSession(questionId).catch(() => {});
 
      // Clear parent success flags first so the sync effect cannot re-open success UI.
      setVideoUploadSuccess?.(prev => {
        const next = { ...prev };
        delete next[questionId];
        return next;
      });
      setPreAssignedImageUrls(prev => {
        const newUrls = { ...prev };
        delete newUrls[questionId];
        return newUrls;
      });

      // Reset all states
      setRecordedUrl(null);
      setRecording(false);
      setPaused(false);
      setTimer(0);
      setShowDelete(false);
      setShowSave(false);
      setShowRecorder(false);
      setUploadSuccess(false);
      chunksRef.current = [];
      setDeleteClicked(true);
      // Clear all media states
      setMediaUrls(prev => {
        const newUrls = { ...prev };
        delete newUrls[questionId];
        return newUrls;
      });
      setVideoBlobs(prev => {
        const newBlobs = { ...prev };
        delete newBlobs[questionId];
        return newBlobs;
      });
      setSelectedFiles(prev => {
        const newFiles = { ...prev };
        delete newFiles[questionId];
        return newFiles;
      });
 
      // Stop and clear any active streams
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
        setStream(null);
      }
 
      // Reset media recorder
      if (mediaRecorder) {
        if (mediaRecorder.state !== 'inactive') {
          mediaRecorder.stop();
        }
        setMediaRecorder(null);
      }
 
      // Force a re-render by setting a small timeout
      setTimeout(() => {
        setShowRecorder(false);
      }, 0);
     
      // Reset mediaUploading when video is deleted
      if (setMediaUploading) {
        setMediaUploading(false);
      }
    } catch (error) {
      console.error('Error deleting media:', error);
      alert('Failed to delete the recording. Please try again.');
    }
  };
 
  const handleRecordAgain = () => {
    // Clear current video
    clearVideoUploadSession(questionId).catch(() => {});
    setUploadProgress(0);
    if (setPreAssignedImageUrls) {
      setPreAssignedImageUrls(prev => {
        const newUrls = { ...prev };
        delete newUrls[questionId];
        return newUrls;
      });
    }
    setMediaUrls(prev => {
      const newUrls = { ...prev };
      delete newUrls[questionId];
      return newUrls;
    });
    setVideoBlobs(prev => {
      const newBlobs = { ...prev };
      delete newBlobs[questionId];
      return newBlobs;
    });
    setSelectedFiles(prev => {
      const newFiles = { ...prev };
      delete newFiles[questionId];
      return newFiles;
    });
    clearErrorMessage();
    setShowSave(false);
    setUploadSuccess(false);
    setRecordedUrl(null);
    setRecording(false);
    setPaused(false);
    setTimer(0);
    setShowDelete(false);
    setShowRecorder(false);
    setPreviewFromUpload(false);
    setMediaRecorder(null);
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    setStream(null);
    chunksRef.current = [];
    // Reset mediaUploading when user starts recording again
    if (setMediaUploading) {
      setMediaUploading(false);
    }
  };
  /**
   * Match VideoResponseUpload (screen upload) `handleFileChange` + `openGalleryPicker`:
   * - Fresh programmatic `<input>` each open (append to body, remove on change).
   * - `URL.createObjectURL(file)` only — no arrayBuffer() (breaks many Android content:// picks).
   * - Looser MIME than strict `video/*` only (Android camera often sends "" or octet-stream).
   * - 150ms delay on Android before applying preview (same idea as iOS delay in screen upload).
   */
  const applyNativeVideoPreview = file => {
    const previewUrl = URL.createObjectURL(file);
    setMediaUrls(prev => ({ ...prev, [questionId]: previewUrl }));
    setVideoBlobs(prev => ({ ...prev, [questionId]: file }));
    setSelectedFiles(prev => ({ ...prev, [questionId]: file }));
    setRecordedUrl(previewUrl);
    setPreviewFromUpload(true);
    setShowSave(true);
    setShowRecorder(false);
    setUploadProgress(0);
  };
 
  const handleNativeVideoSelected = e => {
    const file = e.target?.files?.[0];
    if (!file) return;
 
    const looksLikeVideo =
      !file.type ||
      file.type.startsWith('video/') ||
      file.type === 'application/octet-stream';
    const extOk = /\.(mp4|webm|mkv|3gp|mov|avi|m4v)(\?.*)?$/i.test(file.name || '');
    if (!looksLikeVideo && !extOk) {
      alert('Please select a valid video file.');
      return;
    }
 
    const isAndroid = /Android/i.test(navigator.userAgent || '');
    if (isAndroid) {
      setTimeout(() => applyNativeVideoPreview(file), 150);
    } else {
      applyNativeVideoPreview(file);
    }
  };
 
  const openNativeCamera = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'video/*,video/mp4,video/webm,video/3gpp,video/quicktime';
    input.setAttribute('capture', 'environment');
    input.style.display = 'none';
    document.body.appendChild(input);
    input.addEventListener('change', ev => {
      handleNativeVideoSelected(ev);
      document.body.removeChild(input);
    });
    input.click();
  };
  const openVideoFilePicker = () => {
    if (!disabled) return;
    if (typeof onRequestStartRecording === 'function') {
      const ok = onRequestStartRecording(questionId);
      if (!ok) return;
    }
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'video/*,video/mp4,video/webm,video/3gpp,video/quicktime,.mp4,.webm,.mov,.m4v';
    input.style.display = 'none';
    document.body.appendChild(input);
    input.addEventListener('change', ev => {
      handleNativeVideoSelected(ev);
      if (input.parentNode) document.body.removeChild(input);
    });
    input.click();
  };
 
  const handleRecordClick = () => {
    if (!disabled) return;
    if (typeof onRequestStartRecording === 'function') {
      const ok = onRequestStartRecording(questionId);
      if (!ok) return;
    }
    // if (isMobileDevice()) {
    //   openNativeCamera();
    //   return;
    // }
    handleStartRecorder();
  };
 
  const handleStartRecorder = async () => {
    try {
      await checkAvailableCameras();
      // Start with front camera by default if available, otherwise try back camera
      const initialFacingMode = availableCameras.front ? 'user' : 'environment';
      setFacingMode(initialFacingMode);
 
      const newStream = await navigator.mediaDevices.getUserMedia({
       video: {
          facingMode: initialFacingMode,
          // Enhanced compression settings for smaller file sizes
          width: { ideal: 480, max: 640 }, // Reduced resolution for better compression
          height: { ideal: 270, max: 360 }, // Reduced resolution for better compression
          frameRate: { ideal: 12, max: 15 }, // Lower frame rate = smaller files
          aspectRatio: { ideal: 16/9 }, // Maintain aspect ratio
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: { ideal: 22050 }, // Lower sample rate for smaller files
          channelCount: { ideal: 1 } // Mono audio instead of stereo
        }
 
      });
 
      setStream(newStream);
      setShowRecorder(true);
      setRecordedUrl(null);
      setTimer(0);
    } catch (err) {
      console.error('Media error:', err);
      alert('Please allow camera and microphone access.');
      handleCloseOverlay();
    }
  };
 
  const flipCamera = async () => {
    if (stream) {
      const newFacingMode = facingMode === 'user' ? 'environment' : 'user';
 
      // Check if the target camera is available
      if (
        (newFacingMode === 'user' && !availableCameras.front) ||
        (newFacingMode === 'environment' && !availableCameras.back)
      ) {
        alert(`Camera not available. Please use the ${facingMode === 'user' ? 'front' : 'back'} camera.`);
        return;
      }
 
      // Stop all tracks of the current stream
      stream.getTracks().forEach(track => track.stop());
 
      setFacingMode(newFacingMode);
 
      try {
        const newStream = await navigator.mediaDevices.getUserMedia({
               video: {
          facingMode: newFacingMode,
          // Enhanced compression settings for smaller file sizes
          width: { ideal: 480, max: 640 }, // Reduced resolution for better compression
          height: { ideal: 270, max: 360 }, // Reduced resolution for better compression
          frameRate: { ideal: 12, max: 15 }, // Lower frame rate = smaller files
          aspectRatio: { ideal: 16/9 }, // Maintain aspect ratio
        },
       audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: { ideal: 22050 }, // Lower sample rate for smaller files
          channelCount: { ideal: 1 } // Mono audio instead of stereo
        }
        });
 
        setStream(newStream);
 
        if (recording && mediaRecorder) {
          const tracks = mediaRecorder.stream.getTracks();
          const newTracks = newStream.getTracks();
 
          const videoTrack = newTracks.find(track => track.kind === 'video');
          if (videoTrack) {
            tracks.forEach(track => {
              if (track.kind === 'video') {
                mediaRecorder.stream.removeTrack(track);
                mediaRecorder.stream.addTrack(videoTrack);
              }
            });
          }
        }
      } catch (err) {
        console.error('Error flipping camera:', err);
        alert('Error switching camera. Please try again.');
        // Revert to previous camera if flip fails
        setFacingMode(facingMode);
        handleStartRecorder();
      }
    }
  };
 
  const startRecording = () => {
    if (!stream) {
      console.error('Stream not initialized!');
      return;
    }
 
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
 
    // Use iOS-specific MIME type if on iOS, otherwise use standard MIME type
    const mimeType = isIOS
      ? 'video/mp4'
      : MediaRecorder.isTypeSupported('video/webm;codecs=vp8')
      ? 'video/webm;codecs=vp8'
      : 'video/webm';
 
    // Enhanced compression settings for smaller file sizes
    const recorder = new MediaRecorder(stream, {
      mimeType,
      videoBitsPerSecond: 500_000, // Reduced from 500k to 300k for better compression
      audioBitsPerSecond: 42_000, // Reduced from 64k to 32k for better compression
    });
 
    setMediaRecorder(recorder);
    chunksRef.current = [];
 
    recorder.ondataavailable = e => {
      if (e.data.size > 0) {
        chunksRef.current.push(e.data);
      }
    };
 
    recorder.onstop = () => {
      if (wasDeletedRef.current) {
        wasDeletedRef.current = false;
      } else {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        const url = URL.createObjectURL(blob);
 
        setMediaUrls(prev => ({ ...prev, [questionId]: url }));
        setVideoBlobs(prev => ({ ...prev, [questionId]: blob }));
        setRecordedUrl(url);
        setPreviewFromUpload(false);
        setShowSave(true);
      }
    };
 
    recorder.start(1000);
    setRecording(true);
    setPaused(false);
  };
 
  const handleStart = () => {
    startRecording();
    setShowDelete(true);
  };
 
  const handleCloseOverlay = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    if (mediaRecorder) {
      mediaRecorder.stop();
      setMediaRecorder(null);
    }
    setShowRecorder(false);
    setRecordedUrl(null);
    setRecording(false);
    setPaused(false);
    setTimer(0);
  };
 
  const handleUpload = async questionId => {
    // const connection = navigator.connection;
    // const connectionLooksDown =
    //   Boolean(connection) && (connection.metered || (typeof connection.downlink === 'number' && connection.downlink < 1));
    if (!navigator.onLine) {
      showTempErrorMessage('Network is down. Please try connecting to another network');
      return;
    }
    setIsUploading(true);
    setChance1(false);
    setMediaUploading(true);
    setChance2(false);
    setNetDown(false);
    setCheckFileApiMessageStage(null);
    clearErrorMessage();
    if (typeof onClearUploadActionError === 'function') onClearUploadActionError();
    setUploadProgress(0);
    setRemainingUploadSeconds(null);
    setUploadStage('preparing');
 
    try {
      await uploadVideo(
        questionId,
        13,
        undefined,
        ({ attempt }) => {
          setCheckFileApiMessageStage(attempt);
        },
        (fraction, meta) => {
          setUploadProgress(Math.min(1, Math.max(0, Number(fraction) || 0)));
          if (meta?.stage) setUploadStage(meta.stage);
          if (meta?.stage === 'uploading') {
            if (Number.isFinite(meta.remainingSeconds)) {
              setRemainingUploadSeconds(meta.remainingSeconds);
            }
          } else {
            setRemainingUploadSeconds(null);
          }
        }
      );
      setUploadSuccess(true);
      setCheckFileApiMessageStage(null);
      setRemainingUploadSeconds(null);
      setIsUploading(false);
    } catch (error) {
      console.error('Final upload error:', error);
      setUploadSuccess(false);
      const failMessage =
        error?.message ||
        (error?.isNetworkError
          ? 'Network error. Please check your connection and try again.'
          : 'Video upload failed. Please try again.');

      // Parent already showed the 3 connection messages (+ 5s "Video upload failed").
      setIsUploading(false);
      setMediaUploading(false);
      setCheckFileApiMessageStage(null);
      if (!error?.connectionMessagesShown) {
        showTempErrorMessage(failMessage);
      }
      if (Number.isFinite(savedUploadProgress)) {
        setUploadProgress(Math.min(1, Math.max(0, savedUploadProgress)));
      }
    }
  };
 
  useEffect(() => {
    let attempts = 3;
 
    const checkConnection = async () => {
      const isConnected = await checkInternetConnection();
      if (!isConnected) {
        if (attempts === 3) {
          setChance1(true);
          setChance2(false);
          setNetDown(false);
        } else if (attempts === 2) {
          setChance1(false);
          setChance2(true);
          setNetDown(false);
        } else if (attempts === 1) {
          setChance1(false);
          setChance2(false);
          setNetDown(true);
        }
 
        if (attempts > 0) {
          console.log(`Connection lost. Retrying... (${attempts} attempts left)`);
          attempts--;
          // Wait 5 seconds before next attempt
          setTimeout(() => {
            if (isUploading || videoUploading) {
              checkConnection();
            }
          }, 5000);
        } else {
          // Keep uploading UI so multipart failure can show the 3 staged
          // messages, then Retry/Delete. Do not jump straight to resume.
          setChance1(false);
          setChance2(false);
          setNetDown(true);
          if (connectionCheckInterval.current) {
            clearInterval(connectionCheckInterval.current);
          }
        }
      } else {
        // Reset states if connection is restored
        setChance1(false);
        setChance2(false);
        setNetDown(false);
        attempts = 3;
      }
    };
 
    if (isUploading || videoUploading) {
      // Check connection immediately
      checkConnection();
 
      // Then check every 5 seconds
      connectionCheckInterval.current = setInterval(checkConnection, 5000);
    }
 
    // Cleanup interval on unmount or when upload completes
    return () => {
      if (connectionCheckInterval.current) {
        clearInterval(connectionCheckInterval.current);
      }
    };
  }, [isUploading, videoUploading]);

  useEffect(() => {
    if (!canResumeVideoUpload || typeof onResumeVideoUpload !== 'function') return undefined;
    const handleOnline = () => {
      if (navigator.onLine) {
        onResumeVideoUpload();
      }
    };
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [canResumeVideoUpload, onResumeVideoUpload]);

  // Parent may finish CheckFileInS3 via auto-resume (child isUploading stays false).
  // Sync local success so Record Again / Submit cannot replace the success UI.
  // When parent clears both flags (delete), drop local success — do not force deleteclicked false
  // while successMsg is still stale, or trash requires two clicks.
  useEffect(() => {
    if (preAssignedImageUrl || successMsg) {
      setUploadSuccess(true);
      setDeleteClicked(false);
      setIsUploading(false);
      setCheckFileApiMessageStage(null);
    } else {
      setUploadSuccess(false);
    }
  }, [preAssignedImageUrl, successMsg]);
 
  // Parent progress is source of truth (esp. auto-resume). Never Math.max with
  // stale local values or a prior upload's 100% sticks after delete + reupload.
  useEffect(() => {
    if (
      (isUploading || videoUploading || canResumeVideoUpload) &&
      Number.isFinite(savedUploadProgress)
    ) {
      setUploadProgress(Math.min(1, Math.max(0, savedUploadProgress)));
    }
  }, [isUploading, videoUploading, canResumeVideoUpload, savedUploadProgress]);

  useEffect(() => {
    if (!canResumeVideoUpload && !isUploading && !videoUploading) {
      // Incomplete upload was deleted / cleared — drop local % so next upload starts clean.
      if (savedUploadProgress === 0) {
        setUploadProgress(0);
      }
    }
  }, [canResumeVideoUpload, isUploading, videoUploading, savedUploadProgress]);

  const activeConnectionStage =
    connectionMessageStage != null ? connectionMessageStage : checkFileApiMessageStage;
  const networkErrorTextStyle = {
    color: '#f44336',
    fontSize: '12px',
    marginTop: '4px',
  };

  // Keep progress UI while parent drives the 3 connection messages.
  if (isUploading || videoUploading || activeConnectionStage != null) {
    const activeUploadStage = uploadStageProp || uploadStage;
    const activeRemainingSeconds = Number.isFinite(remainingUploadSecondsProp)
      ? remainingUploadSecondsProp
      : remainingUploadSeconds;
    const remainingTimeLabel = formatRemainingUploadTime(activeRemainingSeconds);
    const displayProgress = Number.isFinite(savedUploadProgress)
      ? savedUploadProgress
      : Number.isFinite(uploadProgress)
        ? uploadProgress
        : 0;
    const uploadPercent = Math.min(100, Math.round(Math.min(1, Math.max(0, displayProgress)) * 100));
    return (
      <VideoUploadContainer>
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <div style={{ marginBottom: '10px' }}>
            <div
              style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                background: `conic-gradient(#9ebe21 ${uploadPercent * 3.6}deg, #f3f3f3 0deg)`,
                margin: '0 auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <div
                style={{
                  width: '52px',
                  height: '52px',
                  borderRadius: '50%',
                  background: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  color: '#666',
                }}
              >
                {uploadPercent}%
              </div>
            </div>
          </div>
          {activeConnectionStage == null && !netDown && !chance1 && !chance2 && (
            <>
              <div style={{ color: '#9ebe21', fontWeight: 500 }}>
                {activeUploadStage === 'preparing'
                  ? 'Preparing video'
                  : activeUploadStage === 'confirming'
                    ? 'video confirming'
                    : 'Video uploading'}
              </div>
              {activeUploadStage === 'uploading' && remainingTimeLabel && (
                <div style={{ color: '#666', fontSize: '13px', marginTop: '6px' }}>
                  {remainingTimeLabel}
                </div>
              )}
            </>
          )}
          {activeConnectionStage != null ? (
            <>
              {activeConnectionStage === 1 && (
                <div style={{ color: 'red', fontWeight: 500 }}>Your connection is down...</div>
              )}
              {activeConnectionStage === 2 && (
                <div style={{ color: 'red', fontWeight: 500 }}>It is taking longer than usual...</div>
              )}
              {activeConnectionStage === 3 && (
                <div style={{ color: 'red', fontWeight: 500 }}>Video upload failed</div>
              )}
            </>
          ) : (
            <>
              {chance1 && (
                <div style={{ color: 'red', fontWeight: 500 }}>Your connection is down...</div>
              )}
              {chance2 && (
                <div style={{ color: 'red', fontWeight: 500 }}>It is taking longer than usual...</div>
              )}
              {netDown && (
                <div style={{ color: 'red', fontWeight: 500 }}>
                  Try connecting internet to upload video...
                </div>
              )}
            </>
          )}
          {errorMessage && <div style={{ ...networkErrorTextStyle, marginTop: '10px' }}>{errorMessage}</div>}
        </div>
      </VideoUploadContainer>
    );
  }

  // Prefer success over Retry / Record Again once CheckFileInS3 succeeded.
  if ((preAssignedImageUrl || uploadSuccess || successMsg) && !deleteclicked) {
    return (
      <VideoUploadContainer>
        <SuccessRow>
          <SuccessMessage>&#10003; Video uploaded successfully.</SuccessMessage>
          <DeleteButton onClick={handleDelete}>
            <FaTrash />
          </DeleteButton>
        </SuccessRow>
      </VideoUploadContainer>
    );
  }

  if (canResumeVideoUpload) {
    const resumePercent = Math.min(
      100,
      Math.round((Number.isFinite(savedUploadProgress) ? savedUploadProgress : 0) * 100)
    );
    const resumeError = errorMessage || uploadActionError;
    return (
      <VideoUploadContainer>
        <div style={{ textAlign: 'center', padding: '20px' }}>
          {videoUrl && (
            <VideoPreview controls playsInline src={videoUrl} style={{ marginBottom: '12px' }} />
          )}
          <div style={{ color: '#666', marginBottom: '8px' }}>
            Upload paused at {resumePercent}%
          </div>
          {resumeError ? (
            <div style={{ color: '#f44336', fontSize: '12px', marginTop: '4px', marginBottom: '16px' }}>
              {resumeError}
            </div>
          ) : (
            <div style={{ marginBottom: '16px' }} />
          )}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            <ButtonSubmit
              variant="primary"
              onClick={() => {
                if (!navigator.onLine) {
                  showTempErrorMessage('No connection. Please connect to the internet and try again.');
                  return;
                }
                clearErrorMessage();
                if (typeof onClearUploadActionError === 'function') onClearUploadActionError();
                if (typeof onResumeVideoUpload === 'function') onResumeVideoUpload();
              }}
            >
              Retry
            </ButtonSubmit>
            <ButtonSubmit
              variant="primary"
              onClick={() => {
                if (!navigator.onLine) {
                  showTempErrorMessage('No connection. Please connect to the internet and try again.');
                  return;
                }
                clearErrorMessage();
                setUploadProgress(0);
                if (typeof onClearUploadActionError === 'function') onClearUploadActionError();
                if (typeof onDeleteIncompleteVideoUpload === 'function') {
                  onDeleteIncompleteVideoUpload();
                }
              }}
            >
              Delete
            </ButtonSubmit>
          </div>
        </div>
      </VideoUploadContainer>
    );
  }
 
  return (
    <VideoUploadContainer
      style={{
        opacity: disabled ? 1 : 0.5,
        pointerEvents: disabled ? 'auto' : 'none'
      }}
    >
      {!videoUrl && !showRecorder && !preAssignedImageUrl && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            gap: '28px',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <UploadButton
              onClick={handleRecordClick}
              style={{
                width: 'auto',
                minWidth: '72px',
                cursor: disabled ? 'pointer' : 'not-allowed',
              }}
            >
              <FaVideo />
            </UploadButton>
            <p style={{ marginTop: '8px', marginBottom: 0 }}>Record</p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <UploadButton
              onClick={openVideoFilePicker}
              style={{
                width: 'auto',
                minWidth: '72px',
                cursor: disabled ? 'pointer' : 'not-allowed',
              }}
            >
              <FaUpload />
            </UploadButton>
            <p style={{ marginTop: '8px', marginBottom: 0 }}>Upload</p>
          </div>
        </div>
      )}
 
      {videoUrl && !uploadSuccess && (
        <>
          <VideoPreview controls playsInline src={videoUrl} />
          {errorMessage && (
            <div
              style={{
                color: '#f44336',
                fontSize: '12px',
                marginTop: '4px',
                marginBottom: '10px',
                textAlign: 'center',
              }}
            >
              {errorMessage}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <ButtonSubmit variant="primary" onClick={handleRecordAgain}>
              {previewFromUpload ? 'Upload Again' : 'Record Again'}
            </ButtonSubmit>
            <ButtonSubmit variant="primary" onClick={() => handleUpload(questionId)}>
              Submit
            </ButtonSubmit>
          </div>
        </>
      )}
 
      {showRecorder && disabled && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: '#000',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            touchAction: 'none',
            overscrollBehavior: 'none',
            userSelect: 'none'
          }}
        >
          {!recording && !recordedUrl && (
            <CloseButton onClick={handleCloseOverlay}>
            <span className="cross" />
            </CloseButton>
          )}
 
          {recordedUrl ? (
            <VideoPreview controls src={recordedUrl} style={{ flex: 1, objectFit: 'cover' }} />
          ) : (
            <VideoPreview ref={videoRef} autoPlay playsInline muted style={{ flex: 1, objectFit: 'cover' }} />
          )}
 
          <TimerDisplay>{formatTime(timer)}</TimerDisplay>
 
          {!recording && !recordedUrl && (
            <ButtonContainer>
              <FlipButton
                onClick={flipCamera}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FaSync />
              </FlipButton>
              <CenterButton>
                <RoundButton
                  onClick={handleStart}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#9ebe21'
                  }}
                >
                  <FaPlay />
                </RoundButton>
              </CenterButton>
              <div style={{ width: '46px' }}></div> {/* Spacer to balance the layout */}
            </ButtonContainer>
          )}
 
          {recording && (
              <ButtonContainer>
              <div
                onClick={handleDeleteuri}
                style={{
                  color: '#fff',
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(4px)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.3)',
                    transform: 'scale(1.1)'
                  }
                }}
              >
                <FaTrash />
              </div>
 
              {!paused && (
                <RoundButton onClick={pauseRecording} style={{ marginLeft: '30px' }}>
                  <FaPause />
                </RoundButton>
              )}
              {paused && (
                <RoundButton onClick={resumeRecording} style={{ marginLeft: '30px' }}>
                  <FaPlay />
                </RoundButton>
              )}
              <GreenRoundButton
                onClick={() => {
                  stopVideoRecording(questionId);
                  handleCloseOverlay();
                  // Preview state ("Record Again / Submit") should NOT block navigation.
                  // Only block when upload is actually in progress (handled by handleUpload()).
                  if (setMediaUploading) {
                    setMediaUploading(false);
                  }
                }}
              >
                Save
              </GreenRoundButton>
              </ButtonContainer>    
          )}
        </div>
      )}
    </VideoUploadContainer>
  );
};
 
export default VideoUploadQuestion;
